import { Component, OnInit, ViewChild, ElementRef, EventEmitter, Output } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { ApiResponse } from '../../../../interfaces/api-response.interface';
import { map } from 'rxjs/operators';

import { StateInterface, Locale } from '../../../../store/state.model';
import { ReportsService, IUsageReportItem, RegistrationService, AuthenticationService, UsersService, User } from '../../../../services';
import { RecipesHelper } from '../../../../helpers/recipes/recipes.helper';
import { BaseComponent } from '../../../base/base.component';
import { _, getState, Moment } from '../../../../tools';

const Flatpickr = require('flatpickr');
const Dutch = require('flatpickr/dist/l10n/nl.js').nl;

@Component({
	template: require('./report-usage-view.component.html')
})

/**
 * Class representing the RecipeViewComponent component.
 */
export class ReportUsageViewComponent extends BaseComponent implements OnInit {
	public activity: IUsageReportItem[];
	public filteredActivity: IUsageReportItem[];

	/**
 * The users
 * @type {User[]}
 */
	users: User[];

	/**
 * The filtered users
 * @type {User[]}
 */
	filteredUsers: User[];
	selectedUser: any = 'all';
	selectedOption: any = 'all';
	public lateOptions: any = ['Telaat', 'Optijd'];

	public startDate: string = Moment().startOf('day').format('YYYY-MM-DD');
	public endDate: string = Moment().endOf('day').format('YYYY-MM-DD');
	@ViewChild('dateInputStart', { static: true }) dateInputStart: ElementRef;
	@ViewChild('dateInputEnd', { static: true }) dateInputEnd: ElementRef;
	private datePickerStart: any;
	private datePickerEnd: any;


	private locale: Locale;

	/**
	 * Constructor.
	 * @param {LayoutService} layoutService
	 * @param {Store} store
	 * @return {void}
	 */
	constructor(
		private reportsService: ReportsService,
		private registrationService: RegistrationService,
		private authenticationService: AuthenticationService,
		private usersService: UsersService,
		private store: Store<StateInterface>,
	) {
		super();

		this.usersService.getUsers().pipe(
			map((res: Response) => this.authenticationService.doStoreBearer(res)))
			.subscribe((data: ApiResponse) => {
				const { success, result } = data;
				this.users = _.cloneDeep(result);
				this.filteredUsers = this.users.sort((a, b) => a.FirstName < b.FirstName ? -1 : 1);
			}, (error: any) => {
				console.log('error', error);
			});

		this.getReport();

		// Subscribes to the locale state
		this.addSubscription(store.pipe(select('locale'))
			.subscribe((locale: Locale) => {
				this.locale = _.cloneDeep(locale);
			})
		);

		const flatPickrConfigStart = {
			dateFormat: 'Y-m-d',
			altFormat: 'd-m-Y',
			altInput: true,
			defaultDate: this.startDate,
			maxDate: this.endDate,
			onChange: (selectedDates, dateStr, instance) => {
				this.startDate = dateStr;
				this.datePickerEnd.set('minDate', this.startDate);
				this.getReport();
			}
		};
		setTimeout(() => {
			this.datePickerStart = new Flatpickr(
				this.dateInputStart.nativeElement,
				this.locale.current === 'nl'
					? { ...flatPickrConfigStart, locale: Dutch }
					: flatPickrConfigStart
			);
		});

		const flatPickrConfigEnd = {
			dateFormat: 'Y-m-d',
			altFormat: 'd-m-Y',
			altInput: true,
			defaultDate: this.startDate,
			minDate: this.startDate,
			onChange: (selectedDates, dateStr, instance) => {
				this.endDate = dateStr;
				this.datePickerStart.set('maxDate', this.endDate);
				this.getReport();
			}
		};
		setTimeout(() => {
			this.datePickerEnd = new Flatpickr(
				this.dateInputEnd.nativeElement,
				this.locale.current === 'nl'
					? { ...flatPickrConfigEnd, locale: Dutch }
					: flatPickrConfigEnd
			);
		});
	}

	private getReport(): void {
		this.activity = undefined;
		this.reportsService.getUsageReport(Moment(this.startDate).startOf('day').unix(), Moment(this.endDate).endOf('day').unix()).subscribe((data: any) => {
			if (data && data._body) {
				let response: any = JSON.parse(data._body);
				if (response.success) {
					this.activity = response.result || [];
					this.filteredActivity = this.selectedUser !== 'all' ? this.activity.filter(x => x.CabinetUser === this.selectedUser) : this.activity;
				}
			}
		});
	}

	/**
* Emit apply event to the parent component.
* @param {object} $event
* @return {void}
*/
	onApply(userId): void {
		this.selectedUser = userId;
		this.onCheck(this.selectedOption);
	}

	onCheck(option): void {
		this.selectedOption = option;
		this.filteredActivity = this.selectedUser !== 'all' ? this.activity.filter(x => x.CabinetUser === this.selectedUser) : this.activity;
		if (this.selectedOption !== 'all') {
			if (this.selectedOption === 'Telaat') {
				this.filteredActivity = this.filteredActivity.filter(f => f.PutDT > f.EndDT);
			} else if (this.selectedOption === 'Optijd') {
				this.filteredActivity = this.filteredActivity.filter(f => (f.PutDT < f.EndDT) || (f.PutDT === f.EndDT));
			}
		}
	}

	/**
	 * Upon initializing the component.
	 * @return {void}
	 */
	ngOnInit(): void {
		window.scrollTo(0, 0);
	}

	public setStateToReturned(item: IUsageReportItem): void {
		item.resetClicked = true;
		this.registrationService.updateRegistration(item.ID, {
			Status: 4
		}).subscribe(() => {
			item.Status = 4;
		}, (error: any) => {
			console.log('error', error);
		});
	}

	/**
* Apply a search query filter.
* @param {string} query
*/
	applyFilter(query: string): void {
		this.filteredUsers = this.users.filter(user => {
			const { FirstName, LastName } = user;
			const hasQuery = (haystack, needle) => haystack.search(new RegExp(needle, 'i')) !== -1;

			if (query) {
				// Searches user metadata
				let inFirstName = hasQuery(FirstName, query);
				let inLastName = hasQuery(LastName, query);

				return (inFirstName || inLastName) || false;
			}
			return true;
		});

		this.selectedUser = this.filteredUsers !== null ? this.onApply(this.filteredUsers[0]) : this.selectedUser;
	}

}
